import { DialogComponent } from '@theme/dialog';

/**
 * A dialog that lets shoppers add a free personal engraving to a product.
 * The textarea already carries the `properties[Engraving]` name and the
 * buy-buttons form's `form` attribute, so its value is submitted with the
 * order automatically - this component only needs to manage the live
 * character count, the live preview, and the trigger link's label.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLInputElement} input
 * @property {HTMLElement} charCount
 * @property {HTMLElement} previewText
 * @property {HTMLButtonElement} trigger
 *
 * @extends DialogComponent
 */
export class EngravingDialog extends DialogComponent {
  requiredRefs = ['dialog', 'input', 'charCount', 'previewText', 'trigger'];

  connectedCallback() {
    super.connectedCallback();
    this.#sync();
  }

  /**
   * Updates the character count and live preview as the shopper types.
   */
  handleInput = () => {
    this.#sync();
  };

  /**
   * Commits the current input value, updates the trigger link's label, and
   * closes the dialog.
   */
  handleSave = () => {
    const { input, trigger } = this.refs;
    const value = input.value.trim();
    input.value = value;

    const addLabel = trigger.dataset.addLabel ?? '';
    const editLabelTemplate = trigger.dataset.editLabelTemplate ?? '[value]';

    trigger.textContent = value ? editLabelTemplate.replace('[value]', value) : addLabel;
    trigger.classList.toggle('has-engraving', Boolean(value));

    this.#sync();
    this.closeDialog();
  };

  /**
   * Clears the engraving entirely.
   */
  handleClear = () => {
    const { input, trigger } = this.refs;
    input.value = '';
    trigger.textContent = trigger.dataset.addLabel ?? '';
    trigger.classList.remove('has-engraving');
    this.#sync();
  };

  #sync() {
    const { input, charCount, previewText } = this.refs;
    const max = input.maxLength > 0 ? input.maxLength : 0;
    const remaining = Math.max(max - input.value.length, 0);

    charCount.textContent = String(remaining);
    previewText.textContent = input.value || previewText.dataset.placeholder || '';
    previewText.classList.toggle('is-placeholder', !input.value);
  }
}

if (!customElements.get('engraving-dialog')) {
  customElements.define('engraving-dialog', EngravingDialog);
}
